import React, { useEffect, useState } from "react";
import { Building2, Users, FileText, TrendingUp, Ticket, AlertCircle, MessageSquare, CreditCard } from "lucide-react";
import api from "@/lib/api";
import { Link } from "react-router-dom";

function Stat({ icon: Icon, label, value, accent, testId, to }) {
  const content = (
    <>
      <div className="flex items-center justify-between mb-5">
        <p className="eyebrow">{label}</p>
        <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
          <Icon className="w-4 h-4 text-slate-900" />
        </div>
      </div>
      <p className="text-5xl font-bold tracking-tight text-slate-900">{value ?? "—"}</p>
      {accent && <p className="text-xs text-slate-500 mt-2">{accent}</p>}
    </>
  );
  return to ? (
    <Link to={to} className="bento p-6 block hover:shadow-lg" data-testid={testId}>{content}</Link>
  ) : (
    <div className="bento p-6" data-testid={testId}>{content}</div>
  );
}

export default function AdminOverview() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    })();
  }, []);

  const s = stats || {};
  const hasAlerts = (s.cancel_requests || 0) + (s.upgrade_requests || 0) + (s.open_feature_requests || 0) > 0;

  return (
    <div className="p-8 md:p-12 animate-fade-up">
      <p className="eyebrow mb-2">Platform overview</p>
      <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-10" data-testid="admin-overview-title">Mission Control</h1>

      {hasAlerts && (
        <div className="bento p-6 mb-8 bg-amber-50 border-amber-200 flex items-start gap-4" data-testid="alerts-banner">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-amber-900 mb-2">Items need your attention</p>
            <div className="flex flex-wrap gap-2">
              {s.upgrade_requests > 0 && <Link to="/admin/agencies" className="bg-white hover:bg-amber-100 text-amber-900 text-sm px-3 py-1.5 rounded-full inline-flex items-center gap-1 border border-amber-200" data-testid="alert-upgrades">{s.upgrade_requests} upgrade request{s.upgrade_requests > 1 ? "s" : ""} →</Link>}
              {s.cancel_requests > 0 && <Link to="/admin/agencies" className="bg-white hover:bg-amber-100 text-amber-900 text-sm px-3 py-1.5 rounded-full inline-flex items-center gap-1 border border-amber-200" data-testid="alert-cancels">{s.cancel_requests} cancellation request{s.cancel_requests > 1 ? "s" : ""} →</Link>}
              {s.open_feature_requests > 0 && <Link to="/admin/feedback" className="bg-white hover:bg-amber-100 text-amber-900 text-sm px-3 py-1.5 rounded-full inline-flex items-center gap-1 border border-amber-200" data-testid="alert-feedback">{s.open_feature_requests} open feature request{s.open_feature_requests > 1 ? "s" : ""} →</Link>}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Stat icon={Building2} label="Agencies" value={s.total_agencies} accent={`${s.suspended_agencies || 0} suspended`} testId="admin-stat-agencies" to="/admin/agencies" />
        <Stat icon={TrendingUp} label="Signups · 30d" value={s.signups_30d} accent="Last month" testId="admin-stat-signups" />
        <Stat icon={Users} label="Total clients" value={s.total_clients} accent="Across all agencies" testId="admin-stat-clients" />
        <Stat icon={FileText} label="Contracts signed" value={s.contracts_signed} accent={`of ${s.total_contracts || 0} total`} testId="admin-stat-contracts" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Stat icon={CreditCard} label="Upgrade requests" value={s.upgrade_requests} accent="Waiting for your approval" testId="admin-stat-upgrade-reqs" to="/admin/agencies" />
        <Stat icon={AlertCircle} label="Cancel requests" value={s.cancel_requests} accent="Need your review" testId="admin-stat-cancel-reqs" to="/admin/agencies" />
        <Stat icon={MessageSquare} label="Feature requests" value={s.open_feature_requests} accent="Open or under review" testId="admin-stat-feature-reqs" to="/admin/feedback" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bento p-8 lg:col-span-2" data-testid="admin-recent-signups">
          <p className="eyebrow mb-4">Recent signups</p>
          {(s.recent_signups || []).length === 0 ? (
            <p className="text-sm text-slate-500">No agencies yet.</p>
          ) : (
            <div className="space-y-3">
              {(s.recent_signups || []).map((a) => (
                <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0 border-slate-100">
                  <div>
                    <p className="font-medium text-slate-900">{a.name}</p>
                    <p className="text-xs text-slate-500 font-mono">{a.owner_email}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs px-2 py-0.5 bg-slate-100 rounded-full font-mono">{a.appsumo_tier || "Tier1"}</span>
                    <p className="text-xs text-slate-400 mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bento p-8 bg-slate-900 text-white grain-overlay" data-testid="admin-coupon-summary">
          <div className="flex items-center gap-3 mb-4">
            <Ticket className="w-5 h-5" />
            <p className="eyebrow text-slate-400">Coupons</p>
          </div>
          <p className="text-5xl font-bold mb-1">{s.active_coupons ?? "—"}</p>
          <p className="text-sm text-slate-400">of {s.total_coupons ?? 0} active</p>
          <Link to="/admin/coupons" className="mt-6 inline-block text-sm underline text-white/80 hover:text-white">Manage coupons →</Link>
        </div>
      </div>
    </div>
  );
}
